import type { GuidebookHouseRules } from "../types"

interface Props {
  houseRules: GuidebookHouseRules
}

interface RuleRow {
  emoji: string
  label: string
  detail?: string
}

function showInGuidebook(rule: { enabled: boolean; surfaces?: { guidebook?: boolean } } | undefined): boolean {
  if (!rule?.enabled) return false
  return rule.surfaces?.guidebook !== false
}

function friendlyValue(val?: string): string {
  switch (val) {
    case "not_allowed": return "Not permitted"
    case "allowed": return "Allowed"
    case "allowed_outside": return "Allowed outside only"
    case "approval_required": return "Please ask your host first"
    case "designated_areas": return "Designated areas only"
    default: return val || "Please check with your host"
  }
}

function friendlySafety(rule: string): string {
  switch (rule) {
    case "fire_extinguisher": return "Fire extinguisher available"
    case "first_aid_kit": return "First aid kit available"
    case "smoke_detector": return "Smoke detector installed"
    case "carbon_monoxide_detector": return "Carbon monoxide detector installed"
    case "security_camera_exterior": return "Security camera on exterior"
    default: return rule.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
  }
}

function buildRuleRows(rules: GuidebookHouseRules): RuleRow[] {
  const rows: RuleRow[] = []

  if (showInGuidebook(rules.quietHours)) {
    const qh = rules.quietHours!
    const parts: string[] = []
    if (qh.startTime && qh.endTime) parts.push(`${qh.startTime}–${qh.endTime}`)
    if (qh.strictness === "strict") parts.push("Please keep noise to a minimum")
    rows.push({ emoji: "🔇", label: "Quiet hours", detail: parts.join(" · ") || undefined })
  }

  if (showInGuidebook(rules.smoking)) {
    const s = rules.smoking!
    const parts = [friendlyValue(s.value)]
    if (s.notes) parts.push(s.notes)
    rows.push({ emoji: "🚭", label: "Smoking", detail: parts.join(" · ") })
  }

  if (showInGuidebook(rules.pets)) {
    const p = rules.pets!
    const parts = [friendlyValue(p.value)]
    if (p.notes) parts.push(p.notes)
    rows.push({ emoji: "🐾", label: "Pets", detail: parts.join(" · ") })
  }

  if (showInGuidebook(rules.visitors)) {
    const v = rules.visitors!
    const parts = [friendlyValue(v.value)]
    if (v.notes) parts.push(v.notes)
    rows.push({ emoji: "👥", label: "Visitors", detail: parts.join(" · ") })
  }

  if (showInGuidebook(rules.parties)) {
    const pt = rules.parties!
    const parts = [friendlyValue(pt.value)]
    if (pt.notes) parts.push(pt.notes)
    rows.push({ emoji: "🎉", label: "Parties & events", detail: parts.join(" · ") })
  }

  if (rules.waste?.enabled) {
    const parts: string[] = []
    if (rules.waste.binLocation) parts.push(`Bins: ${rules.waste.binLocation}`)
    if (rules.waste.recyclingTypes?.length) parts.push(`Recycle: ${rules.waste.recyclingTypes.join(", ")}`)
    if (rules.waste.notes) parts.push(rules.waste.notes)
    if (parts.length) rows.push({ emoji: "♻️", label: "Waste & recycling", detail: parts.join(" · ") })
  }

  if (rules.safety?.enabled) {
    const safetyItems: string[] = []
    if (rules.safety.enabledRules?.length) {
      for (const r of rules.safety.enabledRules) safetyItems.push(friendlySafety(r))
    }
    if (rules.safety.customRules?.length) {
      for (const r of rules.safety.customRules) safetyItems.push(r)
    }
    for (const item of safetyItems) {
      rows.push({ emoji: "⚠️", label: item })
    }
  }

  if (rules.parking?.instruction) {
    rows.push({ emoji: "🅿️", label: "Parking", detail: rules.parking.instruction })
  }

  if (rules.outdoor?.rules?.length) {
    const parts = [...rules.outdoor.rules]
    if (rules.outdoor.notes) parts.push(rules.outdoor.notes)
    rows.push({ emoji: "🌿", label: "Outdoor areas", detail: parts.join(" · ") })
  }

  if (rules.custom?.rules?.length) {
    for (const r of rules.custom.rules) {
      rows.push({ emoji: "📋", label: r })
    }
  }

  return rows
}

export function HouseRulesSection({ houseRules }: Props) {
  const rows = buildRuleRows(houseRules)

  if (rows.length === 0) return null

  return (
    <div className="gc-card gc-card--divided">
      {rows.map((row, i) => (
        <div key={i} className="gc-rule-row">
          <div className="gc-rule-icon">{row.emoji}</div>
          <div>
            <div className="gc-rule-label">{row.label}</div>
            {row.detail && <div className="gc-rule-detail">{row.detail}</div>}
          </div>
        </div>
      ))}
    </div>
  )
}
